import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import {
  Tag,
  Sparkle,
  Clock,
  Warning,
  CheckCircle,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABELS = {
  alle: 'Alle',
  kosmetolog: 'Kosmetolog',
  frisør: 'Frisør',
  andet: 'Øvrige',
};

function formatPrice(price) {
  if (price === null || price === undefined) return null;
  return `${Math.round(price).toLocaleString('da-DK')} kr`;
}

function formatWhen(iso) {
  if (!iso) return 'ukendt';
  try {
    return new Date(iso).toLocaleString('da-DK', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return 'ukendt';
  }
}

export function Services() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('alle');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setError(null);
      const res = await axios.get(`${API}/services-catalog`);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching services catalog:', err);
      setError('Kunne ikke hente ydelser. Prøv at opdatere.');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      setError(null);
      const res = await axios.post(`${API}/services-catalog/refresh`);
      setData(res.data);
    } catch (err) {
      console.error('Error refreshing services catalog:', err);
      setError('Opdatering fra Gecko mislykkedes. Viser den seneste kendte liste.');
    } finally {
      setRefreshing(false);
    }
  };

  // Stable reference per fetch — `data?.services || []` built a new array on every
  // render, which changed the deps of both useMemo hooks below and defeated them.
  const services = useMemo(() => data?.services || [], [data]);

  const stats = useMemo(() => {
    const withPrice = services.filter((s) => s.price !== null && s.price !== undefined);
    return {
      total: services.length,
      withPrice: withPrice.length,
      withoutPrice: services.length - withPrice.length,
      notOnline: services.filter((s) => !s.onlineBookable).length,
    };
  }, [services]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return services.filter((s) => {
      if (kind !== 'alle' && s.kind !== kind) return false;
      if (!q) return true;
      return (
        (s.name || '').toLowerCase().includes(q) ||
        String(s.serviceId).includes(q)
      );
    });
  }, [services, query, kind]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="services-page">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-heading font-bold text-zinc-900 tracking-tight">
            {t('services')}
          </h1>
          <p className="text-zinc-500 mt-1">
            Hentet fra Gecko og gemt i vores egen database i {data?.ttl_hours ?? 24} timer.
            {' '}Sidst opdateret {formatWhen(data?.fetched_at)}.
          </p>
        </div>
        <Button
          onClick={refresh}
          disabled={refreshing}
          className="gap-2"
          data-testid="refresh-catalog"
        >
          <ArrowCounterClockwise size={16} weight="bold" className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Opdaterer…' : 'Opdater fra Gecko'}
        </Button>
      </div>

      {data?.stale && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <Warning size={20} weight="duotone" className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-900">
            Gecko svarede ikke ved seneste opdatering. Listen herunder er den seneste
            kendte og kan være forældet.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <Warning size={20} weight="duotone" className="text-red-600 mt-0.5 shrink-0" />
          <p className="text-sm text-red-900">{error}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {[
          // Full class strings — Tailwind cannot see dynamically built names like
          // `bg-${tone}-100` and would purge them from the build.
          { icon: Tag, box: 'bg-indigo-100', fg: 'text-indigo-600', value: stats.total, label: 'Ydelser i alt' },
          { icon: CheckCircle, box: 'bg-emerald-100', fg: 'text-emerald-600', value: stats.withPrice, label: 'Med pris' },
          { icon: Warning, box: 'bg-amber-100', fg: 'text-amber-600', value: stats.withoutPrice, label: 'Uden pris' },
          { icon: Sparkle, box: 'bg-zinc-100', fg: 'text-zinc-600', value: stats.notOnline, label: 'Ikke online booking' },
        ].map((card) => (
          <Card key={card.label} className="bg-white border-zinc-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg ${card.box} flex items-center justify-center`}>
                  <card.icon size={24} weight="duotone" className={card.fg} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-zinc-900">{card.value}</p>
                  <p className="text-sm text-zinc-500">{card.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Søg efter ydelse eller id…"
          className="max-w-xs bg-white"
          data-testid="services-search"
        />
        <div className="flex gap-1">
          {Object.keys(KIND_LABELS).map((k) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                kind === k
                  ? 'bg-zinc-900 text-white'
                  : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50'
              }`}
            >
              {KIND_LABELS[k]}
            </button>
          ))}
        </div>
        <span className="text-sm text-zinc-500 ml-auto">
          Viser {filtered.length} af {services.length}
        </span>
      </div>

      {/* Table */}
      <Card className="bg-white border-zinc-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ydelse</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Varighed</TableHead>
                <TableHead className="text-right">Pris</TableHead>
                <TableHead>Online booking</TableHead>
                <TableHead className="text-right">Id</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-zinc-500 py-10">
                    Ingen ydelser matcher søgningen.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((s) => (
                <TableRow key={s.serviceId} data-testid={`service-${s.serviceId}`}>
                  <TableCell className="font-medium text-zinc-900">{s.name}</TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={
                        s.kind === 'frisør'
                          ? 'bg-violet-100 text-violet-700'
                          : s.kind === 'kosmetolog'
                          ? 'bg-sky-100 text-sky-700'
                          : 'bg-zinc-100 text-zinc-600'
                      }
                    >
                      {KIND_LABELS[s.kind] || s.kind}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-zinc-600 whitespace-nowrap">
                    {s.duration ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock size={14} weight="duotone" className="text-zinc-400" />
                        {s.duration} min
                      </span>
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-zinc-900 whitespace-nowrap">
                    {formatPrice(s.price) || (
                      <span
                        className="text-amber-600 font-normal inline-flex items-center gap-1"
                        title="Ingen pris registreret i Gecko"
                      >
                        <Warning size={14} weight="duotone" />
                        ingen pris
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {s.onlineBookable ? (
                      <span className="text-emerald-600 text-sm inline-flex items-center gap-1">
                        <CheckCircle size={14} weight="duotone" /> Ja
                      </span>
                    ) : (
                      <span className="text-zinc-400 text-sm">Kun telefon</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-zinc-400 text-sm">{s.serviceId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
